import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Swords, Loader2, Coins, Target, UserPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { showPopup } from "@/components/CenterPopup";

export default function JoinInvite() {
  const { code } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/rooms/invite/${code}`).then(({ data }) => {
      if (data.players?.some((p) => p.user_id === user.id)) nav(`/room/${data.id}`, { replace: true });
      else setRoom(data);
    }).catch((e) => setError(formatApiError(e.response?.data?.detail) || "Invite not found"));
  }, [code, user.id, nav]);

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rooms/join-code", { code });
      window.dispatchEvent(new Event("wallet:refresh"));
      nav(`/room/${data.id}`, { replace: true });
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      if (/insufficient/i.test(detail)) showPopup({ type: "wallet", title: "Insufficient balance", message: "Add coins to join this room." });
      else showPopup({ type: "warning", title: "Can't join", message: detail });
    } finally { setBusy(false); }
  };

  if (error) return (
    <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="invite-error">
      <h2 className="font-display font-bold text-xl mb-2">Invite unavailable</h2>
      <p className="text-white/50 text-sm mb-6">{error}</p>
      <button onClick={() => nav("/home")} className="px-6 py-3 rounded-full bg-pazer-red font-bold">Go to game</button>
    </div>
  );
  if (!room) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-pazer-red" /></div>;

  const host = room.players?.find((p) => p.user_id === room.host_id)?.username || "A friend";
  const open = room.status === "OPEN" || room.status === "FULL";
  const Icon = room.mode === "1v1" ? Swords : Users;
  return (
    <div className="mx-auto max-w-md px-4 py-10" data-testid="join-invite-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl pz-glass border border-pazer-red/40 p-7 text-center">
        <div className="mx-auto mb-4 h-16 w-16 rounded-2xl bg-pazer-red/15 flex items-center justify-center"><UserPlus className="h-8 w-8 text-pazer-red" /></div>
        <p className="text-white/60 text-sm"><span className="text-white font-semibold" data-testid="invite-host">{host}</span> invited you to</p>
        <h1 className="font-display font-extrabold text-2xl mt-1 mb-5 flex items-center justify-center gap-2"><Icon className="h-6 w-6 text-pazer-red" />{room.mode === "1v1" ? "1v1 Duel" : "Multiplayer Room"}</h1>
        <div className="grid grid-cols-3 gap-2 mb-6">
          <Stat icon={Target} label="Target" value={`${room.target}s`} />
          <Stat icon={Coins} label="Entry" value={room.entry_fee} />
          <Stat icon={Users} label="Players" value={`${room.current_players}/${room.max_players}`} />
        </div>
        {open ? (
          <button onClick={join} disabled={busy} data-testid="invite-join-btn"
            className="w-full py-3.5 rounded-full bg-pazer-red hover:bg-pazer-redhover font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Pay {room.entry_fee} &amp; Join
          </button>
        ) : <p className="text-pazer-red text-sm" data-testid="invite-closed">This room is no longer open to join.</p>}
        <p className="text-xs text-white/40 mt-3 font-mono-pz">Code {room.room_code}</p>
      </motion.div>
    </div>
  );
}

function Stat({ icon: I, label, value }) {
  return (
    <div className="bg-pazer-deep/40 rounded-xl p-3">
      <I className="h-4 w-4 text-pazer-red mx-auto mb-1" />
      <div className="font-mono-pz font-bold">{value}</div>
      <div className="text-[10px] text-white/40 uppercase">{label}</div>
    </div>
  );
}
